import { useState } from 'react';
import { Pressable } from 'react-native';

import { SectionCard } from '@/components/SectionCard';
import { Text } from '@/components/Text';
import { spacing } from '@/constants/theme';

export type DealAboutCardProps = {
  /** The deal's long-form `description`. */
  text: string;
  testID?: string;
};

/** Lines shown before "Read more". */
const COLLAPSED_LINES = 4;
/**
 * Roughly four lines of body text on a phone. Below this there's nothing to
 * hide, so no toggle. A character count rather than a layout measurement:
 * deterministic on web and native, and a near-miss only means a short
 * description shows a toggle that reveals a few words.
 */
const COLLAPSE_AT_CHARS = 180;

/**
 * "About this deal": the long description, collapsed to a few lines so the
 * reward and the steps stay near the top of the page. A short description
 * shows in full with no toggle.
 */
export function DealAboutCard({ text, testID }: DealAboutCardProps) {
  const [expanded, setExpanded] = useState(false);
  const collapsible = text.length > COLLAPSE_AT_CHARS;

  return (
    <SectionCard title="About this deal" testID={testID}>
      <Text numberOfLines={collapsible && !expanded ? COLLAPSED_LINES : undefined}>{text}</Text>
      {collapsible ? (
        <Pressable
          onPress={() => setExpanded((v) => !v)}
          hitSlop={spacing.sm}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          testID={testID ? `${testID}-toggle` : undefined}
        >
          <Text variant="bodyBold" color="accentText">
            {expanded ? 'Show less' : 'Read more'}
          </Text>
        </Pressable>
      ) : null}
    </SectionCard>
  );
}
